import { Distribution, DistributionParam, GraphNodeType } from "@custom-types/graphs"
import { ReactFlowReturnType } from "@features/builder/graph/changeGroup"
import { getNestedGroupsIds } from "@features/builder/graph/changeGroup/moveToGroup"
import { AppNode, EdgeData, GroupNode, NodeType } from "@features/builder/graph/types"
import {
  distributionDefaultValues,
  expandedGroupMinHeight,
  expandedGroupMinWidth,
  groupPadding,
  nodeHeight,
  nodeWidth,
} from "@features/builder/helpers/nodes"
import { nodesGroupMapper } from "@features/builder/helpers/nodesGroupMapper"
import { NodeSize } from "@features/builder/types"
import { BaseGraphMetaZipNode } from "@store/services/converter/types"
import logger from "@utils/logger"
import { Edge, EdgeMarker, EdgeMarkerType, Node, Position, getNodesBounds } from "@xyflow/react"
import { XYPosition } from "@xyflow/system"

export function findMaxId(nodes: Node[], isGroup?: boolean): string {
  const nodesToConsider = nodes.filter((node) =>
    isGroup ? node.type === NodeType.Group : node.type !== NodeType.Group,
  )
  const nodeNumericIds = nodesToConsider.map((node) => Number(node.id.replace(/\D/g, "")))

  // 0 + 1 in case there is no groups / nodes
  const id = Math.max(...[0, ...nodeNumericIds]) + 1

  return isGroup ? `g${id}` : `n${id}`
}

export function sortNodes(nodes: AppNode[]) {
  return nodes.toSorted((a, b) => {
    return (a.data.nestingLevel || 0) - (b.data.nestingLevel || 0)
  })
}

export function updateSubNodesForGroup(
  targetTopLevelNode: AppNode,
  groupNode: GroupNode,
  allNodes: AppNode[],
): AppNode[] {
  const allNodesUpdatedMap = new Map(allNodes.map((node) => [node.id, structuredClone(node)]))
  const childrenNodes = Array.from(allNodesUpdatedMap.values()).filter(
    (node) => node.parentId === groupNode.id,
  )

  childrenNodes.forEach((childNode) => {
    childNode.data.nestingLevel = (groupNode.data.nestingLevel || 1) + 1
    childNode.extent = "parent"
    allNodesUpdatedMap.set(childNode.id, childNode)

    if (childNode.type === NodeType.Group) {
      const updatedNodes = updateSubNodesForGroup(
        targetTopLevelNode,
        childNode,
        Array.from(allNodesUpdatedMap.values()),
      )
      updatedNodes.forEach((updatedNode) => allNodesUpdatedMap.set(updatedNode.id, updatedNode))
    } else {
      childNode.data.metaNode.groups = getNestedGroupsIds(
        groupNode,
        Array.from(allNodesUpdatedMap.values()),
      )
      allNodesUpdatedMap.set(childNode.id, childNode)
    }
  })

  return Array.from(allNodesUpdatedMap.values())
}

export const removeNodesFromGroup = (
  nodes: AppNode[],
  allNodes: AppNode[],
  getInternalNode: ReactFlowReturnType["getInternalNode"],
): AppNode[] => {
  const updatedNodesMap = new Map(allNodes.map((n) => [n.id, structuredClone(n)]))

  nodes.forEach((node) => {
    //  this node is not in a group, don't change anything
    if (!node.parentId) return

    const parentNode = updatedNodesMap.get(node.parentId)
    const updatedNode = updatedNodesMap.get(node.id)
    const internalNode = getInternalNode(node.id)

    if (!parentNode || !updatedNode || !internalNode) {
      logger.error(`Could not remove node ${node.id} from group ${node.parentId}`)
      return
    }

    updatedNode.data.nestingLevel = 1
    updatedNode.parentId = undefined
    updatedNode.extent = undefined
    updatedNode.position = internalNode.internals.positionAbsolute

    if (updatedNode.type === NodeType.Group) {
      updatedNode.data.metaGroup.parent = undefined
      // recursively update nested groups "nestingLevel" and non-groups node "node.data.metaNode.groups"
      const updateNestedNodes = (groupId: string, currentLevel: AppNode) => {
        const childNodes = Array.from(updatedNodesMap.values()).filter(
          (n) => n.parentId === groupId,
        )

        childNodes.forEach((childNode) => {
          const updatedChild = updatedNodesMap.get(childNode.id)
          if (!updatedChild) return

          updatedChild.data.nestingLevel = currentLevel.data.nestingLevel + 1

          if (updatedChild.type === NodeType.Group) {
            updateNestedNodes(updatedChild.id, updatedChild)
          } else {
            // collect all parent group ids from the most inner to outer
            const parentGroups: string[] = []
            let currentParentId = updatedChild.parentId

            while (currentParentId) {
              parentGroups.push(currentParentId)
              const parentNode = updatedNodesMap.get(currentParentId)
              currentParentId = parentNode?.parentId
            }

            updatedChild.data.metaNode.groups = parentGroups
          }

          updatedNodesMap.set(updatedChild.id, updatedChild)
        })
      }

      updateNestedNodes(updatedNode.id, updatedNode)
    } else {
      updatedNode.data.metaNode.groups = []
    }

    updatedNodesMap.set(updatedNode.id, updatedNode)
  })

  return Array.from([...updatedNodesMap.values()])
}

export function isMarkerEndCorrect(markerEnd: EdgeMarkerType | undefined): markerEnd is EdgeMarker {
  return !!markerEnd && typeof markerEnd !== "string" && markerEnd.color !== "#FF0000"
}

export const getNestedNodesFromGroup = (
  group: GroupNode,
  allNodes: AppNode[],
  onlyVisible = false,
): AppNode[] => {
  let childrenNodes = allNodes.filter((node) => node.parentId === group.id)

  if (onlyVisible) childrenNodes = childrenNodes.filter((node) => !node.hidden)

  const nestedGroups = childrenNodes.filter(
    (node) => node.type === NodeType.Group && node.parentId === group.id,
  ) as GroupNode[]

  nestedGroups.forEach((nestedGroup) => {
    childrenNodes = [
      ...childrenNodes,
      ...getNestedNodesFromGroup(nestedGroup, allNodes, onlyVisible),
    ]
  })

  return childrenNodes
}

export const calculateExpandedGroupSize = (group: GroupNode, allNodes: AppNode[]): NodeSize => {
  const childrenNodes = getNestedNodesFromGroup(group, allNodes)

  if (childrenNodes.length === 0) {
    return {
      height: expandedGroupMinHeight,
      width: expandedGroupMinWidth,
    }
  }

  // "measured" is required for "getNodesBounds" to correctly calculate the boundary
  const childrenNodesMeasured = childrenNodes.map((node) => ({
    ...node,
    measured: {
      width: Number(node?.style?.width ?? nodeWidth),
      height: Number(node?.style?.height ?? nodeHeight),
    },
  }))
  const { height, width } = getNodesBounds(childrenNodesMeasured)

  return {
    height: height + 2 * groupPadding,
    width: width + 2 * groupPadding,
  }
}

export const collapseNestedGroups = (group: GroupNode, allNodes: AppNode[]): AppNode[] => {
  let updatesNodes = structuredClone(allNodes)
  const nestedGroups = updatesNodes.filter(
    (node) => node.type === NodeType.Group && node.parentId === group.id,
  ) as GroupNode[]

  nestedGroups.forEach((nestedGroup) => {
    updatesNodes = collapseGroup(nestedGroup, updatesNodes)
  })

  return updatesNodes
}

export const recalculateParentGroupsSizes = (node: AppNode, allNodes: AppNode[]): AppNode[] => {
  if (!node.parentId) return allNodes

  const updatedNodes = structuredClone(allNodes)
  const parentGroup = updatedNodes.find(
    (n) => n.type === NodeType.Group && n.id === node.parentId,
  ) as GroupNode | undefined

  if (!parentGroup) return allNodes
  if (parentGroup.data.metaGroup.isCollapsed) return allNodes

  const newStyleSize = calculateExpandedGroupSize(parentGroup, updatedNodes)

  if (
    parentGroup.style?.height !== undefined &&
    newStyleSize.height > parseFloat(String(parentGroup.style.height))
  ) {
    parentGroup.style.height = newStyleSize.height + groupPadding
  }
  if (
    parentGroup.style?.width !== undefined &&
    newStyleSize.width > parseFloat(String(parentGroup.style.width))
  ) {
    parentGroup.style.width = newStyleSize.width + groupPadding
  }

  return recalculateParentGroupsSizes(parentGroup, updatedNodes)
}

export function collapseGroup(group: GroupNode, allNodes: AppNode[]): AppNode[] {
  const mapper = nodesGroupMapper(allNodes)

  const currentGroup = mapper.groupsMap.get(group.id)
  if (currentGroup === undefined) {
    logger.error(`Could not expand group ${group.id} as it is not found`)
    return allNodes
  }

  currentGroup.data.metaGroup.expanded = {
    ...currentGroup.data.metaGroup.expanded,
    width: currentGroup.measured?.width,
    height: currentGroup.measured?.height,
  }

  currentGroup.style = { width: nodeWidth, height: nodeHeight }
  currentGroup.data.metaGroup.isCollapsed = true

  const childNodesId = mapper.groupToNodesIdsMapDeep.get(group.id) ?? []
  const subGroupsId = mapper.parentToChildGroupsIdsMapDeep.get(group.id) ?? []
  for (const id of [...childNodesId, ...subGroupsId]) {
    const childNode = mapper.allMap.get(id)
    if (!childNode) continue

    childNode.hidden = true
  }

  return Array.from(mapper.allMap.values())
}

export function expandGroup(group: GroupNode, allNodes: AppNode[]): AppNode[] {
  const mapper = nodesGroupMapper(allNodes)

  const currentGroup = mapper.groupsMap.get(group.id)
  if (currentGroup === undefined) {
    logger.error(`Could not expand group ${group.id} as it is not found`)
    return allNodes
  }

  currentGroup.measured = {
    ...currentGroup.measured,
    width: currentGroup.data.metaGroup.expanded?.width,
    height: currentGroup.data.metaGroup.expanded?.height,
  }
  currentGroup.style = {
    ...currentGroup.style,
    width: currentGroup.data.metaGroup.expanded?.width,
    height: currentGroup.data.metaGroup.expanded?.height,
  }
  currentGroup.data.metaGroup.expanded = undefined
  currentGroup.data.metaGroup.isCollapsed = false

  const childNodesId = mapper.groupToNodesIdsMapDeep.get(group.id) ?? []
  const subGroupsId = mapper.parentToChildGroupsIdsMapDeep.get(group.id) ?? []

  for (const id of [...childNodesId, ...subGroupsId]) {
    const childNode = mapper.allMap.get(id)
    const parentGroup = mapper.groupsMap.get(childNode?.parentId ?? "")
    if (childNode && !parentGroup?.data.metaGroup.isCollapsed) {
      childNode.hidden = false
    }
  }

  return Array.from([...mapper.allMap.values()])
}

export function highlightSelectedNodes(
  selectedNodes: AppNode[],
  allEdges: Edge<EdgeData>[],
  allNodes: AppNode[],
) {
  if (selectedNodes.length === 1) {
    const node = selectedNodes[0]!
    const sourceNodeIds: string[] = []
    const targetNodeIds: string[] = []

    const updatedEdges = allEdges.map((edge) => {
      const copyEdge = structuredClone(edge)
      if (!copyEdge.className) {
        copyEdge.className = ""
      }

      copyEdge.className = copyEdge.className.replaceAll(" edge-source-selected ", "")
      copyEdge.className = copyEdge.className.replaceAll(" edge-target-selected ", "")

      if (isMarkerEndCorrect(copyEdge.markerEnd)) copyEdge.markerEnd.color = "#B1B1B7"

      if (copyEdge.source === node.id && !copyEdge.className.includes(" edge-source-selected ")) {
        targetNodeIds.push(copyEdge.target)
        copyEdge.className += " edge-source-selected "
        if (isMarkerEndCorrect(copyEdge.markerEnd)) copyEdge.markerEnd.color = "#FF9800"
      }
      if (copyEdge.target === node.id && !copyEdge.className.includes(" edge-target-selected ")) {
        sourceNodeIds.push(copyEdge.source)
        copyEdge.className += " edge-target-selected "
        if (isMarkerEndCorrect(copyEdge.markerEnd)) copyEdge.markerEnd.color = "#648FFF"
      }

      return copyEdge
    })

    const updatedNodes = allNodes.map((node) => {
      const copyNode = structuredClone(node)
      if (!copyNode.className) {
        copyNode.className = ""
      }

      copyNode.className = copyNode.className?.replaceAll(" node-source-selected ", "")
      copyNode.className = copyNode.className?.replaceAll(" node-target-selected ", "")
      if (
        sourceNodeIds.includes(copyNode.id) &&
        !copyNode.className?.includes(" node-source-selected ")
      ) {
        copyNode.className += " node-source-selected "
      }
      if (
        targetNodeIds.includes(copyNode.id) &&
        !copyNode.className?.includes(" node-target-selected ")
      ) {
        copyNode.className += " node-target-selected "
      }
      return copyNode
    })

    return { edges: updatedEdges, nodes: updatedNodes }
  }

  const updatedEdges = allEdges.map((edge) => {
    const copyEdge = structuredClone(edge)
    if (!copyEdge.className) {
      copyEdge.className = ""
    }

    copyEdge.className = copyEdge.className?.replaceAll(" edge-source-selected ", "")
    copyEdge.className = copyEdge.className?.replaceAll(" edge-target-selected ", "")
    if (isMarkerEndCorrect(copyEdge.markerEnd)) copyEdge.markerEnd.color = "#B1B1B7"

    return copyEdge
  })

  const updatedNodes = allNodes.map((node) => {
    const copyNode = structuredClone(node)
    if (!copyNode.className) {
      copyNode.className = ""
    }

    copyNode.className = copyNode.className?.replaceAll(" node-source-selected ", "")
    copyNode.className = copyNode.className?.replaceAll(" node-target-selected ", "")
    return copyNode
  })

  return { edges: updatedEdges, nodes: updatedNodes }
}

export function createEmptyNode(
  type: NodeType,
  maxId: string,
  position: XYPosition,
  isSelected?: boolean,
): AppNode {
  const node = {
    selected: isSelected || false,
    dragging: false,
    hidden: false,
    type: type,
    id: maxId,
    position,
    data: {
      comments: "",
      nestingLevel: 1,
      label: `${maxId}`,
    },
    sourcePosition: Position.Bottom,
    targetPosition: Position.Top,
    style: {
      width: nodeWidth,
      height: nodeHeight,
    },
  }

  const metaNode: BaseGraphMetaZipNode = {
    tags: [],
    label: `${maxId}`,
    color: "#999999",
    "Calibration Mnem": "",
    "Scenario Mnem": "",
    "Forecast Mnem": "",
    "Unit Format": "",
    groups: [],
    "Historic Mnem": "",
    Description: "",
    unit: "",
    comments: "",
    exportExcluded: false,
  }
  switch (type) {
    case NodeType.Empty:
      return {
        ...node,
        data: {
          ...node.data,
          metaNode,
          originalNode: {
            // @ts-ignore
            type: type,
            name: maxId,
            properties: {
              parents_lst: [],
              df_cpd: {},
              a_sample_value: null,
              attributes_lst: [],
            },
          },
        },
      }
    case NodeType.Group:
      return {
        ...node,
        type,
        className: "group-node",
        style: {
          width: expandedGroupMinWidth,
          height: expandedGroupMinHeight,
        },
        data: {
          ...node.data,
          label: maxId,
          nestingLevel: 1,
          metaGroup: {
            nodes: [],
            label: maxId,
            color: "#f6f6f6",
            isCollapsed: false,
          },
        },
      }
    case NodeType.ScenarioTable:
      return {
        ...node,
        type,
        data: {
          ...node.data,
          metaNode,
          originalAttribute: {
            name: maxId,
            type: GraphNodeType.SCENARIO_TABLE,
            properties: {
              index: ["row1"],
              columns: ["col1"],
              data: [[""]],
            },
          },
        },
      }
    case NodeType.ForecastTable:
      return {
        ...node,
        type,
        data: {
          ...node.data,
          metaNode,
          originalAttribute: {
            name: maxId,
            type: GraphNodeType.FORECAST_TABLE,
            properties: {
              index: ["row1"],
              columns: ["col1"],
              data: [[""]],
            },
          },
        },
      }
    case NodeType.Table:
      return {
        ...node,
        type,
        data: {
          ...node.data,
          metaNode,
          originalAttribute: {
            name: maxId,
            type: GraphNodeType.TABLE,
            properties: {
              index: ["row1", "row2"],
              columns: ["col1", "col2"],
              data: [
                ["0", "0"],
                ["0", "0"],
              ],
            },
          },
        },
      }
    case NodeType.Deterministic:
      return {
        ...node,
        type,
        data: {
          ...node.data,
          metaNode,
          originalNode: {
            // @ts-ignore
            type: type,
            name: maxId,
            properties: {
              parents_lst: [],
              df_cpd: {
                expression: ["0"],
                continuous: [true],
              },
              a_sample_value: null,
              attributes_lst: [],
            },
          },
        },
      }
    case NodeType.Lag:
      return {
        ...node,
        type,
        data: {
          ...node.data,
          metaNode,
          originalNode: {
            // @ts-ignore
            type: type,
            name: maxId,
            properties: {
              parents_lst: [],
              df_cpd: {
                expression: [],
                continuous: [true],
              },
              a_sample_value: null,
              attributes_lst: [],
            },
          },
        },
      }
    case NodeType.Scenario:
      return {
        ...node,
        type,
        data: {
          ...node.data,
          metaNode,
          originalNode: {
            // @ts-ignore
            type: type,
            name: maxId,
            properties: {
              parents_lst: [],
              df_cpd: {
                expression: ["0"],
                continuous: [true],
              },
              a_sample_value: "1",
              attributes_lst: [],
            },
          },
        },
      }
    case NodeType.Forecast:
      return {
        ...node,
        type,
        data: {
          ...node.data,
          metaNode,
          originalNode: {
            // @ts-ignore
            type: type,
            name: maxId,
            properties: {
              parents_lst: [],
              df_cpd: {
                expression: ["0"],
                continuous: [true],
              },
              a_sample_value: "1",
              attributes_lst: [],
            },
          },
        },
      }
    case NodeType.Categorical:
      return {
        ...node,
        type,
        data: {
          ...node.data,
          metaNode,
          originalNode: {
            // @ts-ignore
            type: type,
            name: maxId,
            properties: {
              parents_lst: [],
              df_cpd: {
                // ms: sum of probabilities for categorical distribution must equal 1
                distribution: [Distribution.Categorical],
                category1: [0.5],
                category2: [0.5],
              },
              a_sample_value: null,
              attributes_lst: [],
            },
          },
        },
      }
    case NodeType.Continuous:
    case NodeType.Mixture:
      return {
        ...node,
        type,
        data: {
          ...node.data,
          metaNode,
          originalNode: {
            // @ts-ignore
            type: type,
            name: maxId,
            properties: {
              parents_lst: [],
              df_cpd: {
                distribution: [Distribution.Normal],
                loc: [distributionDefaultValues[Distribution.Normal]!.loc ?? null],
                scale: [distributionDefaultValues[Distribution.Normal]!.scale ?? null],
                lbound: [distributionDefaultValues[Distribution.Normal]!.lbound ?? null],
                ubound: [distributionDefaultValues[Distribution.Normal]!.ubound ?? null],
              },
              a_sample_value: null,
              attributes_lst: [],
            },
          },
        },
      }
    case NodeType.Discrete:
      return {
        ...node,
        type,
        data: {
          ...node.data,
          metaNode,
          originalNode: {
            // @ts-ignore
            type: type,
            name: maxId,
            properties: {
              parents_lst: [],
              df_cpd: {
                distribution: [Distribution.Binomial],
                loc: [distributionDefaultValues[Distribution.Binomial]!.loc ?? null],
                n: [distributionDefaultValues[Distribution.Binomial]!.n ?? null],
                p: [distributionDefaultValues[Distribution.Binomial]!.p ?? null],
                lbound: [distributionDefaultValues[Distribution.Binomial]!.lbound ?? null],
                ubound: [distributionDefaultValues[Distribution.Binomial]!.ubound ?? null],
              },
              a_sample_value: null,
              attributes_lst: [],
            },
          },
        },
      }
  }
}

export const getDefaultNodeDfCpd = (type: NodeType) => {
  switch (type) {
    case NodeType.Deterministic:
    case NodeType.Lag:
    case NodeType.Scenario:
    case NodeType.Forecast:
      return {
        expression: ["0"],
        continuous: [true],
      }
    case NodeType.Categorical:
      return {
        distribution: [Distribution.Categorical],
        category1: [0.5],
        category2: [0.5],
      }
    case NodeType.Continuous:
    case NodeType.Mixture:
      return {
        distribution: [Distribution.Normal],
        [DistributionParam.loc]: [distributionDefaultValues[Distribution.Normal]!.loc ?? null],
        [DistributionParam.scale]: [distributionDefaultValues[Distribution.Normal]!.scale ?? null],
        [DistributionParam.lbound]: [
          distributionDefaultValues[Distribution.Normal]!.lbound ?? null,
        ],
        [DistributionParam.ubound]: [
          distributionDefaultValues[Distribution.Normal]!.ubound ?? null,
        ],
      }
    case NodeType.Discrete:
      return {
        distribution: [Distribution.Binomial],
        [DistributionParam.loc]: [distributionDefaultValues[Distribution.Binomial]!.loc ?? null],
        [DistributionParam.n]: [distributionDefaultValues[Distribution.Binomial]!.n ?? null],
        [DistributionParam.p]: [distributionDefaultValues[Distribution.Binomial]!.p ?? null],
        [DistributionParam.lbound]: [
          distributionDefaultValues[Distribution.Binomial]!.lbound ?? null,
        ],
        [DistributionParam.ubound]: [
          distributionDefaultValues[Distribution.Binomial]!.ubound ?? null,
        ],
      }
    default:
      return {}
  }
}
